import { useEffect, useRef, useState } from 'react'

export const PENDING_POLL_INTERVAL_MS = 30_000

/** Ten minutes of polling: long enough for the backend's first scheduled retries. */
export const PENDING_MAX_POLLS = 20

interface PendingTopUp {
  /** A pending receipt is open. */
  active: boolean
  /** The wallet balance as last loaded, or undefined before the first load. */
  balance: number | undefined
  /** Reloads the wallet. */
  refetch: () => unknown
  /** Called once the balance rises above what it was when the receipt opened. */
  onCredited: () => void
  intervalMs?: number
  maxPolls?: number
}

/**
 * While a "payment received, crediting" receipt is open, reloads the wallet every interval
 * and reports when the balance has gone up, so the receipt can say the wallet was credited.
 * A rise is the signal because the page has no per-payment status to ask for; another credit
 * landing at the same time would read the same, which only shows "successful" a little early.
 * A credit that lands before the first reload is not seen as a rise either: the receipt then
 * stays pending until polling stops, and says so.
 *
 * Returns whether polling has stopped without seeing the credit.
 */
export function usePendingTopUp({
  active,
  balance,
  refetch,
  onCredited,
  intervalMs = PENDING_POLL_INTERVAL_MS,
  maxPolls = PENDING_MAX_POLLS
}: PendingTopUp): boolean {
  const startBalance = useRef<number | undefined>(undefined)
  const credited = useRef(false)
  const [exhausted, setExhausted] = useState(false)

  useEffect(() => {
    if (!active) {
      startBalance.current = undefined
      credited.current = false
      setExhausted(false)
      return undefined
    }
    let polls = 0
    const timer = setInterval(() => {
      polls += 1
      // A failed reload is retried on the next tick; it must not surface as an unhandled
      // rejection every 30 seconds.
      Promise.resolve(refetch()).catch(() => undefined)
      if (polls >= maxPolls) {
        clearInterval(timer)
        setExhausted(true)
      }
    }, intervalMs)

    return () => clearInterval(timer)
  }, [active, intervalMs, maxPolls, refetch])

  useEffect(() => {
    if (!active || balance === undefined) {
      return
    }
    if (startBalance.current === undefined) {
      startBalance.current = balance
      return
    }
    if (!credited.current && balance > startBalance.current) {
      credited.current = true
      onCredited()
    }
  }, [active, balance, onCredited])

  return exhausted
}
